import { Button, Spinner } from 'components/common'
import { DynamicInputList } from 'components/common/dynamicInputList'
import {
  BagConfiguration,
  OrderStatusConfiguration,
} from 'components/configuration'
import toast from 'react-hot-toast'
import { InstaPointsConfiguration, PosPaymentMapper } from 'components/mobile'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import {
  ConfigurationWithId,
  MobilePosFormValues,
  PosConfigFormValues,
} from 'types/configuration'
import {
  fetchConfigurations,
  updatePosConfigurations,
} from 'utils/api/configApi'

const PosConfiguration = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [configuration, setConfiguration] = useState<ConfigurationWithId>()

  // Initialize form with simpler structure
  const methods = useForm<PosConfigFormValues>({
    defaultValues: {
      mobile: {
        payments: [],
        orderTypes: [],
        bags: [],
        allowedOrderStatuses: [],
        instaPointsAggregators: [],
      },
      selectedBagSize: '',
      bagBarcodes: {},
      bagPrices: {},
      bagImages: {},
      bagDescriptions: {},
    },
  })

  const getConfig = async () => {
    try {
      setLoading(true)
      const { data } = await fetchConfigurations()

      if (data) {
        setConfiguration(data)

        methods.reset({
          mobile: {
            payments: data.mobile?.payments || [],
            orderTypes: data.mobile?.orderTypes || [],
            bags: data.mobile?.bags || [],
            allowedOrderStatuses: data.mobile?.allowedOrderStatuses || [],
            instaPointsAggregators: data.mobile?.instaPointsAggregators || [],
          },
          selectedBagSize: '',
          bagBarcodes: {},
          bagPrices: {},
          bagImages: {},
          bagDescriptions: {},
        })
      }
    } catch (error) {
      /* empty */
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getConfig()
  }, [])

  if (loading) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center">
        <Spinner size="large" />
      </div>
    )
  }

  if (!configuration) return <div>No Configuration Found</div>

  const { handleSubmit } = methods

  // Transform form data before submission
  const onSubmit = async (data: PosConfigFormValues) => {
    try {
      // Get all bag sizes
      const bagSizes = Object.keys({
        ...data.bagBarcodes,
        ...data.bagPrices,
        ...data.bagImages,
        ...data.bagDescriptions,
      })

      // Create the bags array, preserving all existing bags
      const existingBags = [...data.mobile.bags]

      // Update or add bags with values from the form
      bagSizes
        .filter((size) => size) // Filter out empty sizes
        .forEach((size) => {
          const existingBagIndex = existingBags.findIndex(
            (bag) => bag.size === size,
          )

          if (existingBagIndex >= 0) {
            // Update existing bag
            existingBags[existingBagIndex] = {
              ...existingBags[existingBagIndex],

              barcode:
                data.bagBarcodes[size] ||
                existingBags[existingBagIndex].barcode,

              price: data.bagPrices[size]
                ? Number(data.bagPrices[size])
                : existingBags[existingBagIndex].price,

              img: data.bagImages[size] || existingBags[existingBagIndex].img,

              description:
                data.bagDescriptions[size] ||
                existingBags[existingBagIndex].description,
            }
          } else {
            // Add new bag
            existingBags.push({
              size,
              barcode: data.bagBarcodes[size] || '',
              price: data.bagPrices[size] ? Number(data.bagPrices[size]) : 0,
              img: data.bagImages[size] || '',
              description: data.bagDescriptions[size] || '',
            })
          }
        })

      // Create the final submission data
      const submitData: MobilePosFormValues = {
        mobile: {
          payments: data.mobile.payments,
          orderTypes: data.mobile.orderTypes,
          bags: existingBags,
          allowedOrderStatuses: data.mobile.allowedOrderStatuses,
          instaPointsAggregators: data.mobile.instaPointsAggregators || [],
        },
      }

      await updatePosConfigurations(configuration!._id, submitData)
      toast.success('Configuration updated successfully.')
    } catch (error) {
      // Show error toast if the update fails
      toast.error('Failed to update configuration. Please try again.')
      console.error(error)
    }
  }

  return (
    <div className="space-y-6">
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="rounded-lg border p-4"
        >
          <h2 className="mb-4 text-xl font-bold">Mobile POS Configuration</h2>

          <div className="rounded-lg border border-gray-300 bg-gray-50 p-4">
            <h3 className="mb-4 font-semibold">Order Types</h3>
            <DynamicInputList
              orientation="horizontal"
              label="Order Types"
              name="mobile.orderTypes"
              placeholder="Add Order Type"
              setValue={methods.setValue}
              defaultValues={configuration?.mobile?.orderTypes}
              shouldDelete={false}
            />
          </div>

          <BagConfiguration />

          <PosPaymentMapper />

          <InstaPointsConfiguration />

          <OrderStatusConfiguration />

          <div className="mt-6">
            <Button
              theme="primary"
              type="submit"
              buttonText={'Save All Configurations'}
            />
          </div>
        </form>
      </FormProvider>
    </div>
  )
}

export default PosConfiguration
